import React from 'react';
import { FONT_MONO } from '../../views/landing/landingData';
import { domainsService, type DnsInstruction, type DomainOrderView, type DomainRow } from '../../services/domains.service';
import { Icons } from './studioUi';

/** Shared bits of the custom-domain UI (studio dialog + account billing panel). */

export const fmtIDR = (n: number) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(n);
export const fmtDate = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');

export function statusOf(d: DomainRow): { label: string; bg: string; fg: string } {
    if (d.status === 'ACTIVE' && d.live) return { label: 'LIVE', bg: 'var(--hv-mint)', fg: 'var(--hv-mint-ink)' };
    switch (d.status) {
        case 'ACTIVE': return { label: 'READY', bg: 'var(--hv-mint)', fg: 'var(--hv-mint-ink)' };
        case 'PENDING_DNS': return { label: 'WAITING FOR DNS', bg: 'var(--hv-sand)', fg: 'var(--hv-sand-ink)' };
        case 'PENDING_PAYMENT': return { label: 'AWAITING PAYMENT', bg: 'var(--hv-sand)', fg: 'var(--hv-sand-ink)' };
        case 'REGISTERING': return { label: 'REGISTERING', bg: 'var(--hv-sand)', fg: 'var(--hv-sand-ink)' };
        case 'EXPIRED': return { label: 'EXPIRED', bg: 'var(--hv-danger-bg)', fg: 'var(--hv-danger)' };
        case 'FAILED': return { label: 'NEEDS ATTENTION', bg: 'var(--hv-danger-bg)', fg: 'var(--hv-danger)' };
        default: return { label: d.status, bg: 'var(--hv-page-2)', fg: 'var(--hv-body)' };
    }
}

export const StatusPill: React.FC<{ domain: DomainRow }> = ({ domain }) => {
    const s = statusOf(domain);
    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, height: 22, padding: '0 9px', borderRadius: 999, background: s.bg, color: s.fg, fontFamily: FONT_MONO, fontSize: 9, letterSpacing: '0.1em', whiteSpace: 'nowrap' }}>
            <span style={{ width: 5, height: 5, borderRadius: 999, background: s.fg }} />{s.label}
        </span>
    );
};

/** Small outlined action button used in domain rows. */
export const SmallBtn: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement> & { danger?: boolean }> = ({ danger, style, className, children, ...rest }) => (
    <button
        type="button"
        className={`${danger ? 'hv-h-danger' : 'hv-h-mint-bd'} ${className ?? ''}`}
        style={{ display: 'flex', alignItems: 'center', gap: 6, height: 30, padding: '0 12px', borderRadius: 999, border: '1px solid var(--hv-line-2)', background: 'transparent', color: danger ? 'var(--hv-danger)' : 'var(--hv-body)', fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', ...style }}
        {...rest}
    >
        {children}
    </button>
);

/** The DNS records the owner must add at their registrar, with copy buttons. */
export const RecordsTable: React.FC<{ records: DnsInstruction[]; checks?: { txt: boolean; apex: boolean; www: boolean } | null }> = ({ records, checks }) => {
    const [copied, setCopied] = React.useState<string | null>(null);
    const copy = async (v: string) => {
        try { await navigator.clipboard.writeText(v); setCopied(v); setTimeout(() => setCopied(null), 1500); } catch { /* ignore */ }
    };
    const okFor = (r: DnsInstruction): boolean | null => {
        if (!checks) return null;
        if (r.type === 'TXT') return checks.txt;
        if (r.host === 'www') return checks.www;
        return checks.apex;
    };
    return (
        <div style={{ borderRadius: 14, overflow: 'hidden', border: '1px solid var(--hv-line)', background: 'var(--hv-card)' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '64px 110px minmax(0,1fr) 28px', gap: 10, padding: '8px 14px', background: 'var(--hv-page-2)', borderBottom: '1px solid var(--hv-line)', fontFamily: FONT_MONO, fontSize: 9.5, letterSpacing: '0.14em', color: 'var(--hv-muted)' }}>
                <span>TYPE</span><span>HOST</span><span>VALUE</span><span />
            </div>
            {records.map((r) => {
                const ok = okFor(r);
                // A missing OPTIONAL record (the apex row) must not read as a
                // failure — the site is live from www alone. Muted dash, not a red X.
                const missedOptional = ok === false && r.optional;
                return (
                    <div key={`${r.type}-${r.host}`} style={{ display: 'grid', gridTemplateColumns: '64px 110px minmax(0,1fr) 28px', gap: 10, alignItems: 'center', padding: '10px 14px', borderBottom: '1px solid var(--hv-line)', opacity: r.optional ? 0.75 : 1 }}>
                        <span style={{ fontFamily: FONT_MONO, fontSize: 11.5, fontWeight: 700, color: 'var(--hv-ink)' }}>
                            {r.type}
                            {r.optional && <span style={{ display: 'block', fontSize: 8, letterSpacing: '0.12em', fontWeight: 600, color: 'var(--hv-muted)' }}>OPTIONAL</span>}
                        </span>
                        <span style={{ fontFamily: FONT_MONO, fontSize: 11.5, color: 'var(--hv-body)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.host}>{r.host}</span>
                        <span style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontFamily: FONT_MONO, fontSize: 11.5, color: 'var(--hv-body)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={`${r.value}${r.purpose ? ` — ${r.purpose}` : ''}`}>{r.value}</span>
                            <button type="button" onClick={() => void copy(r.value)} title="Copy" aria-label={`Copy ${r.type} value`} className="hv-h-brand-ink" style={{ flex: 'none', width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 0, padding: 0, background: 'transparent', color: copied === r.value ? 'var(--hv-leaf)' : 'var(--hv-muted)', cursor: 'pointer', borderRadius: 6 }}>
                                {copied === r.value ? Icons.check({ size: 12, sw: 2.8 }) : Icons.copy({ size: 12 })}
                            </button>
                        </span>
                        <span style={{ display: 'flex', justifyContent: 'center', color: ok === null ? 'var(--hv-line-2)' : ok ? 'var(--hv-leaf)' : missedOptional ? 'var(--hv-muted)' : 'var(--hv-danger)' }} title={ok === null ? 'Not checked yet' : ok ? 'Found' : missedOptional ? 'Optional — not added (your site still goes live via www)' : 'Not found yet'}>
                            {ok === null ? <span style={{ width: 8, height: 8, borderRadius: 999, background: 'var(--hv-line-2)' }} /> : ok ? Icons.check({ size: 13, sw: 2.8 }) : missedOptional ? <span style={{ fontSize: 12, fontWeight: 700 }}>–</span> : Icons.x({ size: 13 })}
                        </span>
                    </div>
                );
            })}
            <div style={{ padding: '9px 14px', fontSize: 11.5, lineHeight: 1.55, color: 'var(--hv-muted)' }}>
                Add these at the company where you bought the domain — the TXT and the www CNAME are enough to go live.
                The apex row is optional (use ALIAS/ANAME to the same CNAME target if your DNS provider supports it).
                DNS changes can take up to an hour to show up; check again later if a record is not found yet.
            </div>
        </div>
    );
};

declare global {
    interface Window { snap: any }
}

/**
 * Pay for a domain order, then poll it until it settles (registration/renewal
 * runs server-side right after payment). Resolves with the final order view, or
 * null when the payment was abandoned.
 *
 * The studio document is cross-origin isolated (COEP, required by WebContainers)
 * and COEP blocks Midtrans' Snap iframe outright: the popup never appears and no
 * callback ever fires. There, the payment page opens in a new tab instead
 * (`onRedirect` lets the caller show a link in case the tab was blocked) and the
 * order is polled until paid — `signal` cancels the wait.
 */
export async function payDomainOrder(
    token: string,
    orderId: string,
    onProgress?: (o: DomainOrderView) => void,
    opts: { redirectUrl?: string; onRedirect?: (url: string) => void; signal?: AbortSignal } = {},
): Promise<DomainOrderView | null> {
    if (opts.redirectUrl && (window.crossOriginIsolated || !window.snap)) {
        window.open(opts.redirectUrl, '_blank', 'noopener');
        opts.onRedirect?.(opts.redirectUrl);
        return pollOrder(orderId, 'tab', onProgress, opts.signal);
    }
    if (!window.snap) throw new Error('The payment gateway did not load. Reload the page and try again.');
    const closed = await new Promise<'paid' | 'pending' | 'closed' | 'error'>((resolve) => {
        window.snap.pay(token, {
            onSuccess: () => resolve('paid'),
            onPending: () => resolve('pending'),
            onError: () => resolve('error'),
            onClose: () => resolve('closed'),
        });
    });
    if (closed === 'error') throw new Error('Payment failed. Nothing was charged.');
    return pollOrder(orderId, closed, onProgress, opts.signal);
}

/** Reconcile: the server asks Midtrans, then registers. Poll until it settles. */
async function pollOrder(
    orderId: string,
    mode: 'paid' | 'pending' | 'closed' | 'tab',
    onProgress?: (o: DomainOrderView) => void,
    signal?: AbortSignal,
): Promise<DomainOrderView | null> {
    // A payment in another tab may take a while (VA transfer, QRIS on a phone).
    const deadline = Date.now() + (mode === 'tab' ? 30 * 60_000 : 90_000);
    let last: DomainOrderView | null = null;
    while (Date.now() < deadline && !signal?.aborted) {
        const o = await domainsService.order(orderId);
        if (o) {
            last = o;
            onProgress?.(o);
            if (o.status === 'FULFILLED' || o.status === 'FAILED' || o.status === 'NEEDS_ATTENTION') return o;
            if (o.status === 'PENDING' && mode === 'closed') return o; // not paid (yet)
        }
        await new Promise((r) => setTimeout(r, mode === 'tab' ? 4000 : 2500));
    }
    return signal?.aborted ? null : last;
}
